import React, { useState } from 'react';
import { Form, Input, Button, Space, Modal, message, Upload, Switch } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Logo, LogoRequest } from '../../types/logo';
import { logoService } from '../../services/logoService';

interface LogoFormProps {
  logo?: Logo;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LogoForm: React.FC<LogoFormProps> = ({
  logo,
  isVisible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentLogoFile, setCurrentLogoFile] = useState('');

  const isEditing = !!logo;
  const title = isEditing 
    ? t('logos.form.edit_title') 
    : t('logos.form.create_title');

  const handleSubmit = async (values: LogoRequest) => {
    setIsLoading(true);
    try {
      if (isEditing && logo) {
        await logoService.updateLogo(logo.id, values);
        message.success(t('logos.notifications.update_success'));
      } else {
        await logoService.createLogo(values);
        message.success(t('logos.notifications.create_success'));
      }
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Error submitting logo form:', error);
      message.error(t('logos.notifications.error', { message: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    try {
      const filePath = await logoService.uploadLogoFile(file);
      form.setFieldsValue({ logo_file: filePath });
      setCurrentLogoFile(filePath); // Update state as well
      message.success(t('logos.notifications.upload_success'));
      return false; // Prevent default upload behavior
    } catch (error) {
      console.error('Error uploading file:', error);
      message.error(t('logos.notifications.upload_error'));
      return false;
    } finally {
      setUploading(false);
    }
  };

  // Reset form when modal opens or logo changes
  React.useEffect(() => {
    if (isVisible) {
      const logoFile = logo?.logo_file || '';
      setCurrentLogoFile(logoFile);
      
      if (logo) {
        // Force set field values after a small delay to ensure form is mounted
        const timer = setTimeout(() => {
          form.setFieldsValue({
            logo_name: logo.logo_name || '',
            logo_file: logo.logo_file || '',
            is_default: logo.is_default || false,
          });
        }, 200);
        return () => clearTimeout(timer);
      }
    } else {
      // Reset when modal is closed
      setCurrentLogoFile('');
    }
  }, [form, logo, isVisible]);

  // Debug: log the form values when they change
  React.useEffect(() => {
    if (isVisible && logo) {
      console.log('Logo form opened with data:', logo);
      console.log('Form field value for logo_file (immediate):', form.getFieldValue('logo_file'));
      
      // Check again after a delay
      setTimeout(() => {
        console.log('Form field value for logo_file (after 300ms):', form.getFieldValue('logo_file'));
        console.log('All form values:', form.getFieldsValue());
      }, 300);
    }
  }, [form, logo, isVisible]);


  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={title}
      open={isVisible}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        key={logo?.id || 'new'}
        initialValues={{
          logo_name: logo?.logo_name || '',
          logo_file: logo?.logo_file || '',
          is_default: logo?.is_default || false,
        }}
      >
        <Form.Item
          name="logo_name"
          label={t('logos.form.logo_name')}
          rules={[
            { required: true, message: t('logos.form.logo_name_required') }
          ]}
        >
          <Input placeholder={t('logos.form.logo_name_placeholder')} />
        </Form.Item>

        <Form.Item
          name="logo_file"
          label={t('logos.form.logo_file')}
          rules={[
            { required: true, message: t('logos.form.logo_file_required') }
          ]}
        >
          <Input.Group compact>
            <Input
              value={currentLogoFile}
              onChange={(e) => {
                setCurrentLogoFile(e.target.value);
                form.setFieldsValue({ logo_file: e.target.value });
              }}
              style={{ width: 'calc(100% - 120px)' }}
              placeholder={t('logos.form.logo_file_placeholder')}
              readOnly
            />
            <Upload
              beforeUpload={(file) => {
                // Validate file type (images only)
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error(t('logos.form.invalid_file_type'));
                  return false;
                }

                // Validate file size (5MB max)
                const isLt5M = file.size / 1024 / 1024 < 5;
                if (!isLt5M) {
                  message.error(t('logos.form.file_too_large'));
                  return false;
                }

                handleFileUpload(file);
                return false;
              }}
              showUploadList={false}
              accept="image/*"
            >
              <Button 
                icon={<UploadOutlined />} 
                loading={uploading}
                style={{ width: 120 }}
              >
                {t('logos.form.upload')}
              </Button>
            </Upload>
          </Input.Group>
        </Form.Item>

        <Form.Item
          name="is_default"
          label={t('logos.form.is_default')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {isEditing ? t('common.update') : t('common.create')}
            </Button>
            <Button onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LogoForm;
